import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Pagination, CircularProgress, Chip, Typography, Alert,
} from '@mui/material';
import { fetchCustomerCreditTransactions } from '../api';
import { formatDate, headerCell, surface } from '../format';

const CreditsTab = ({ customerId, refreshKey = 0 }) => {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const res = await fetchCustomerCreditTransactions(customerId, { page, limit: 10 });
                if (cancelled) return;
                setRows(res.data.data || []);
                setTotalPages(res.data.totalPages || 1);
                setTotal(res.data.total || 0);
            } catch (err) {
                if (!cancelled) setError(err.response?.data?.message || 'Failed to load credit history');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [customerId, page, refreshKey]);

    return (
        <Box>
            <Typography sx={{ color: '#6b7280', fontSize: '0.8125rem', mb: 1.5 }}>
                {total} transaction{total === 1 ? '' : 's'}
            </Typography>
            <Paper elevation={0} sx={surface}>
                {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {['Date', 'Action', 'Type', 'Credits', 'Note', 'Order'].map((h) => (
                                        <TableCell key={h} sx={headerCell}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#6b7280' }}>
                                            No credit transactions yet.
                                        </TableCell>
                                    </TableRow>
                                ) : rows.map((row) => {
                                    const isDebit = row.type === 'debit' || (row.credits_deducted || 0) < 0;
                                    const amount = row.credits_deducted ?? row.amount ?? 0;
                                    return (
                                        <TableRow key={row.id} hover>
                                            <TableCell sx={{ color: '#6b7280' }}>{formatDate(row.createdAt)}</TableCell>
                                            <TableCell sx={{ textTransform: 'capitalize' }}>
                                                {(row.action || '').replace(/_/g, ' ')}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={row.type || (isDebit ? 'debit' : 'credit')}
                                                    sx={{
                                                        textTransform: 'capitalize',
                                                        bgcolor: isDebit ? '#fef2f2' : '#ecfdf5',
                                                        color: isDebit ? '#b91c1c' : '#047857',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: isDebit ? '#b91c1c' : '#047857' }}>
                                                {isDebit ? amount : `+${Math.abs(amount)}`}
                                            </TableCell>
                                            <TableCell sx={{ color: '#6b7280', maxWidth: 240 }}>
                                                {row.note || '—'}
                                            </TableCell>
                                            <TableCell sx={{ color: '#6b7280' }}>{row.order_id || '—'}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} shape="rounded" />
                </Box>
            )}
        </Box>
    );
};

export default CreditsTab;
